import { forwardRef, Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { appSettings } from 'app/appSettings';
import { CustomerPermissionsLoadIfSuperUserGuard } from 'app/core/guards/customer-permissions-load-if-superuser.guard';
import { DependencyTreeCanActivate } from 'app/core/guards/dependency-tree.guard';
import { SelectedCustomerGuard } from 'app/core/guards/select-customer-selected.resolve';
import { CustomerInfoSandbox } from 'app/core/sandboxes/customer-info.sandbox';
import { CustomerInfo } from 'app/core/store/customer-info/customer-info.interface';
import { GetDefinitionsGuard } from 'app/core/store/definitions/definitions.resolve';
import * as selectCustomerActions from 'app/core/store/session/selected-customer/select-customer.actions';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { Observable } from 'rxjs';
import * as fromRoot from '../store';
import { BaseGuard } from './base.guard';
import { goAction } from '../store/router/router.actions';
import { map } from 'rxjs/operators';

interface StateDate {
  customerInfo: CustomerInfo;
  selectedCustomer: Customer;
  isSuperUser: boolean;
}

@Injectable({ providedIn: 'root' })
export class CustomerInfoLoadIfNormalUserGuard extends BaseGuard<StateDate> implements DependencyTreeCanActivate {
  private store: Store<fromRoot.RootState>;
  private sandbox: CustomerInfoSandbox;

  constructor(@Inject(forwardRef(() => Store)) store, @Inject(forwardRef(() => CustomerInfoSandbox)) sandbox) {
    super('CustomerInfoLoadIfNormalUserGuard');
    this.store = store;
    this.sandbox = sandbox;
  }

  getState(): Observable<StateDate> {
    return this.store.pipe(
      map(state => {
        return {
          customerInfo: state.customerInfo,
          selectedCustomer: state.session.selectedCustomer.data,
          isSuperUser: state.session.userContext.isSuperUser,
        };
      })
    );
  }

  hasFailed(t: StateDate): boolean {
    return t.customerInfo.failed;
  }

  isLoaded(t: StateDate): boolean {
    return t.isSuperUser || t.customerInfo.loaded;
  }

  isLoading(t: StateDate): boolean {
    return t.customerInfo.loading;
  }

  loader(t: StateDate, params: { [p: string]: string }): void {
    this.sandbox.loadCustomerInfo(t.selectedCustomer);
  }

  resetter(params: { [p: string]: string }): void {
    this.sandbox.resetCustomerInfo();
  }

  versionChecker(t: StateDate, params: { [p: string]: string }): boolean {
    return t.isSuperUser || (t.customerInfo.customer && t.customerInfo.customer.id === (t.selectedCustomer ? t.selectedCustomer.id : ''));
  }

  navigateAway(): void {
    this.sandbox.resetCustomerInfo();
    this.store.dispatch(new selectCustomerActions.ClearSelectedCustomerAction());
    this.store.dispatch(goAction({ path: appSettings.SELECT_CUSTOMER_ROUTE }));
  }

  getDependencies() {
    return [GetDefinitionsGuard, SelectedCustomerGuard, CustomerPermissionsLoadIfSuperUserGuard];
  }

  getDependencyRouteData() {
    return null; // Use original route-data
  }

  didntChange(v1: StateDate, v2: StateDate) {
    return (
      v1.customerInfo.loaded === v2.customerInfo.loaded &&
      v1.customerInfo.loading === v2.customerInfo.loading &&
      v1.customerInfo.failed === v2.customerInfo.failed &&
      (v1.selectedCustomer && v1.selectedCustomer.id) === (v2.selectedCustomer && v2.selectedCustomer.id)
    );
  }
}
